import { FusionViewMode, pairHasPet } from '~/domain/viewport/fusionBlend';

/**
 * How a row draws its pair, as one choice: one of the blend modes the pair
 * holds, or drawn apart one image per pane.
 */
export type PairViewChoice = FusionViewMode | 'sideBySide';

/**
 * The view a row is in, from the two facts that hold it: whether the row
 * hangs its pair apart, and the blend mode of the pair. Apart wins, because
 * nothing blends while the pair is drawn apart, whatever mode the pair holds.
 */
export function pairViewOf(
    sideBySide: boolean,
    mode: FusionViewMode
): PairViewChoice {
    return sideBySide ? 'sideBySide' : mode;
}

/** What choosing a view changes: the row's hanging, the pair's mode, or both. */
export interface PairViewTransition {
    /** Whether the row's side-by-side hanging flips. */
    readonly toggleSideBySide: boolean;
    /** The blend mode to write, when the choice names one the pair does not hold. */
    readonly mode?: FusionViewMode;
}

/**
 * Resolves a view choice against the row's state. The hanging and the mode
 * are two facts: drawing the pair apart is a hanging and leaves the mode
 * as it is, since nothing blends while apart; naming a blend mode writes
 * it and hangs the pair together again. Choosing the view in force
 * changes nothing.
 */
export function pairViewTransition(
    sideBySide: boolean,
    mode: FusionViewMode,
    chosen: PairViewChoice
): PairViewTransition {
    const toggleSideBySide = sideBySide !== (chosen === 'sideBySide');
    if (chosen === 'sideBySide' || chosen === mode) {
        return { toggleSideBySide };
    }
    return { toggleSideBySide, mode: chosen };
}

/**
 * The views a pair may be drawn in, the two together-views first and apart
 * last. Checkerboard needs a second image to interleave and never draws a
 * PET image, whose ramp the renderer applies only over a blend;
 * `pairModalities` is undefined while nothing is fused.
 */
export function pairViewsFor(
    pairModalities: ReadonlyArray<string> | undefined
): ReadonlyArray<PairViewChoice> {
    return pairModalities && !pairHasPet(pairModalities)
        ? ['blend', 'checkerboard', 'sideBySide']
        : ['blend', 'sideBySide'];
}

/**
 * The view after `current` in `choices`, wrapping to the first, so one
 * control steps through every view in the order the list names them. A
 * view the list no longer offers steps to the first.
 */
export function nextPairView(
    current: PairViewChoice,
    choices: ReadonlyArray<PairViewChoice>
): PairViewChoice {
    const index = choices.indexOf(current);
    return choices[(index + 1) % choices.length];
}
